import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  formatCutoffWhen,
  isOpen,
  MEAL_LABELS,
  type MenuOffer,
} from '@mealdesk/shared';
import { EmptyState, PhotoSlot } from '../components/ui.js';
import { useT } from '../lib/i18n.js';
import { useHistory, useMoney, useWeek } from '../lib/ordering.js';

type Status = 'ordered' | 'open' | 'full' | 'closed';

const keyOf = (offer: MenuOffer) => `${offer.serviceDate}|${offer.mealType}|${offer.id}`;

/**
 * The week, as rows.
 *
 * The design gives each day a square thumbnail, the weekday as an overline, the
 * dishes as the headline and one chip carrying the only thing that differs
 * between rows — whether it is open, already ordered, or gone. A grid of large
 * cards was the wrong instinct: this is a list somebody scans for the day they
 * want, not a shelf they browse.
 *
 * It also books several days at once, because that is how people actually use
 * it: they sit down once and put in the whole week rather than opening the app
 * five evenings running.
 */
export default function MenuPage() {
  const navigate = useNavigate();
  const week = useWeek();
  const orders = useHistory();
  const { t } = useT();

  const [tab, setTab] = useState(0);
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);

  const offers = useMemo(() => week.flatMap((day) => day.offers), [week]);

  // Split at the seventh day rather than by calendar week: what matters is
  // "the days I can order now" against "what is coming after that".
  const weeks = useMemo(() => {
    const dates = [...new Set(offers.map((o) => o.serviceDate))].sort();
    const boundary = dates[7];
    return boundary
      ? [offers.filter((o) => o.serviceDate < boundary), offers.filter((o) => o.serviceDate >= boundary)]
      : [offers];
  }, [offers]);

  const orderedKeys = useMemo(
    () =>
      new Set(
        orders
          .filter((o) => o.status !== 'cancelled' && o.status !== 'cancelled_exception')
          .map((o) => `${o.serviceDate}|${o.mealType}`),
      ),
    [orders],
  );

  const shown = weeks[tab] ?? [];
  const range = (list: MenuOffer[]) => {
    if (list.length === 0) return '';
    const dates = list.map((o) => o.serviceDate).sort();
    const fmt = (d: string) =>
      new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${fmt(dates[0] ?? '')} – ${fmt(dates[dates.length - 1] ?? '')}`;
  };

  // Only days still open and not already ordered can go into a batch.
  const selectable = offers.filter(
    (o) =>
      isOpen(o.cutoffAt) &&
      !(o.capacityRemaining !== null && o.capacityRemaining < 1) &&
      !orderedKeys.has(`${o.serviceDate}|${o.mealType}`),
  );
  const picked = selectable.filter((o) => chosen.includes(keyOf(o)));
  const money = useMoney();
  // What the batch comes to, as much of it as this organisation discloses.
  const batchTotal = money(
    picked.reduce((sum, o) => sum + o.price, 0),
    picked.reduce((sum, o) => sum + o.employeeShare, 0),
    picked[0]?.currency ?? 'USD',
  );
  // The deadline that runs out first: the one that decides when to press it.
  const soonest = picked.length
    ? formatCutoffWhen([...picked].sort((a, b) => a.cutoffAt.localeCompare(b.cutoffAt))[0]!.cutoffAt)
    : '';

  const toggle = (offer: MenuOffer) => {
    const key = keyOf(offer);
    setChosen((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  return (
    <>
      {/* While picking, the bar at the bottom covers the last two rows, so
          the list grows a floor to scroll past it. */}
      <div
        className={`px-[22px] pt-[calc(22px+env(safe-area-inset-top))] md:px-2 md:pt-7 ${
          picking ? 'pb-[100px]' : 'pb-2'
        }`}
      >
        <h2 className="text-[25px] font-extrabold md:text-[30px]">{t('Weekly menu')}</h2>
        {/* Not "next week opens Friday": nothing enforces that. A kitchen
            publishes when it publishes, so the screen says what is on it. */}
        <span className="text-[13px] md:text-[14.5px] text-muted">
          {range(shown)} · {new Set(shown.map((o) => o.serviceDate)).size} {t('days published')}
        </span>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {weeks.length > 1 &&
            weeks.map((list, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setTab(index)}
                className={`rounded-pill px-4 py-2.5 text-[13px] md:text-[14.5px] font-bold ${
                  tab === index ? 'bg-brand text-white' : 'border border-line-strong bg-white text-ink-soft'
                }`}
              >
                {index === 0 ? t('This week') : range(list)}
              </button>
            ))}

          {/* The whole point of the screen for anyone who books ahead. */}
          {selectable.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setPicking((on) => !on);
                setChosen([]);
              }}
              className={`ml-auto rounded-pill px-4 py-2.5 text-[13px] md:text-[14.5px] font-extrabold ${
                picking ? 'bg-brand text-white' : 'bg-white text-brand'
              }`}
            >
              {picking ? t('Cancel') : t('Book several days')}
            </button>
          )}
        </div>

        {picking && (
          <p className="mt-3 rounded-tile bg-white px-4 py-3 text-[13.5px] text-muted md:text-[15px]">
            {t(
              'Tap the days you want. You choose where they go on the next screen — all together, or one day at a time.',
            )}
          </p>
        )}

      </div>

      <div className="flex flex-col gap-[11px] px-[22px] pt-4 md:grid md:grid-cols-2 md:gap-3.5 md:px-2 md:pt-6">
        {shown.length === 0 && (
          <EmptyState title={t('Nothing published yet')}
            body={t('Nothing is published for these days yet. Your restaurant posts the week when it is ready.')} />
        )}

        {shown.map((offer) => (
          <DayRow
            key={`${offer.serviceDate}-${offer.mealType}-${offer.id}`}
            offer={offer}
            ordered={orderedKeys.has(`${offer.serviceDate}|${offer.mealType}`)}
            picking={picking}
            chosen={chosen.includes(keyOf(offer))}
            onPick={() => toggle(offer)}
          />
        ))}
      </div>

      {/* Sits on the tab bar rather than under it. */}
      {picking && picked.length > 0 && (
        <div className="fixed inset-x-0 bottom-[calc(var(--tabbar)+10px)] z-30 mx-auto max-w-[430px] px-[22px] md:bottom-6">
          <div className="flex items-center gap-3 rounded-control border border-line-strong bg-white px-4 py-3 shadow-[0_10px_30px_rgba(38,64,28,.18)]">
            <span className="min-w-0 flex-1 text-[13.5px] leading-[1.35] md:text-[15px]">
              <b className="block text-[15px] md:text-[16.5px]">
                {picked.length} meal{picked.length === 1 ? '' : 's'}
              </b>
              <span className="block text-muted">
                {batchTotal ? `${batchTotal} · ` : ''}closes soonest {soonest}
              </span>
            </span>
            <button
              type="button"
              onClick={() => navigate('/book', { state: { keys: picked.map(keyOf) } })}
              className="h-11 shrink-0 rounded-control bg-brand px-5 text-[14px] font-extrabold text-white md:text-[15px]"
            >
              {t('Continue')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/*
 * Three states, because there are three: you have it, you can have it, you
 * cannot. An earlier version marked days more than 36 hours out as "Soon",
 * which read as "not yet" — on days that were open for ordering right then,
 * and which are exactly the days somebody booking a week ahead wants.
 */
const CHIP: Record<Status, { label: string; className: string }> = {
  ordered: { label: 'Ordered', className: 'bg-ok-soft text-ok-ink' },
  open: { label: 'Open', className: 'bg-brand text-white' },
  full: { label: 'Full', className: 'bg-warn-soft text-warn-ink' },
  closed: { label: 'Closed', className: 'bg-neutral-soft text-neutral-ink' },
};

function DayRow({
  offer,
  ordered,
  picking,
  chosen,
  onPick,
}: {
  offer: MenuOffer;
  ordered: boolean;
  picking: boolean;
  chosen: boolean;
  onPick: () => void;
}) {
  const { t } = useT();
  const date = new Date(`${offer.serviceDate}T00:00:00`);
  // A kitchen that cooks forty portions has forty. Somebody who finds that out
  // by having their order refused has been told too late.
  const full = offer.capacityRemaining !== null && offer.capacityRemaining < 1;
  const open = isOpen(offer.cutoffAt) && !full;
  const status: Status = ordered ? 'ordered' : full ? 'full' : open ? 'open' : 'closed';
  const chip = CHIP[status];
  const money = useMoney()(offer.price, offer.employeeShare, offer.currency);

  const body = (
    <>
      <div className="size-16 shrink-0 overflow-hidden rounded-[19px]">
        <PhotoSlot label={offer.title} src={offer.photoUrl} height={64} dimmed={!open} />
      </div>

      <div className="min-w-0 flex-1">
        {/* The meal belongs in the overline: a day can carry lunch and dinner,
            and two rows that differ only in a price are two rows nobody can
            tell apart. */}
        <span className="text-[13px] font-extrabold tracking-[0.05em] text-subtle md:text-[14.5px]">
          {date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()} {date.getDate()}
          {' · '}
          {t(MEAL_LABELS[offer.mealType]).toUpperCase()}
        </span>
        <b className="my-1 block text-[15.5px] tracking-[-0.03em] text-pretty md:text-[17px]">
          {offer.title}
        </b>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <span className="min-w-0 truncate text-[13px] text-muted md:text-[14.5px]">
            {offer.restaurantName}
            {money ? ` · ${money}` : ''}
          </span>
          <span
            className={`shrink-0 rounded-pill px-2.5 py-1 text-[13px] font-extrabold md:text-[14.5px] ${chip.className}`}
          >
            {t(chip.label)}
          </span>
        </div>
        {/* When it shuts, in words. A chip cannot carry a deadline. */}
        <span className="mt-1 block text-[13px] text-subtle md:text-[14.5px]">
          {full
            ? t('The kitchen has taken all it can cook for this day.')
            : open
              ? t('Order by {when}', { when: formatCutoffWhen(offer.cutoffAt) })
              : t('Closed {when}', { when: formatCutoffWhen(offer.cutoffAt) })}
        </span>

        {/* Counted down only when it is close enough to matter: "40 left" on
            Monday is noise, "3 left" an hour before the cutoff is not. */}
        {open && offer.capacityRemaining !== null && offer.capacityRemaining <= 10 && (
          <span className="mt-0.5 block text-[13px] font-extrabold text-warn-ink md:text-[14.5px]">
            {t('{count} left', { count: offer.capacityRemaining })}
          </span>
        )}
      </div>
    </>
  );

  const shell = 'flex items-center gap-3.5 rounded-card bg-white px-[15px] py-[13px] text-left';
  const shadow = 'shadow-[0_4px_14px_rgba(38,64,28,.05)]';

  if (picking) {
    const pickable = open && !ordered;
    return (
      <div
        className={`rounded-card bg-white ${shadow} ${
          chosen ? 'ring-2 ring-brand-bright' : ''
        } ${pickable ? '' : 'opacity-55'}`}
      >
        <button
          type="button"
          disabled={!pickable}
          onClick={onPick}
          aria-pressed={chosen}
          className={`${shell} w-full`}
        >
          <span
            aria-hidden
            className={`flex size-[22px] shrink-0 items-center justify-center rounded-full border-2 text-[13px] font-extrabold md:text-[14.5px] ${
              chosen ? 'border-brand bg-brand text-white' : 'border-line-dashed'
            }`}
          >
            {chosen ? '✓' : ''}
          </span>
          {body}
        </button>

      </div>
    );
  }

  return (
    <Link
      to={open ? `/order/${offer.serviceDate}/${offer.mealType}/${offer.id}` : '/menu'}
      className={`${shell} ${shadow} ${open ? '' : 'pointer-events-none'}`}
    >
      {body}
    </Link>
  );
}
